import { FC } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps, PrismicRichText } from "@prismicio/react";
import { PrismicNextImage } from "@prismicio/next";

/**
 * Props for `PeopleGrid`.
 */
export type PeopleGridProps = SliceComponentProps<Content.PeopleGridSlice>;

/**
 * Component for "PeopleGrid" Slices.
 */
const PeopleGrid: FC<PeopleGridProps> = ({ slice }) => {
  const people = slice.primary.people || [];
  const count = people.length;

  // Determine span based on number of images
  let span: number;
  switch (count) {
    case 2:
      span = 6;
      break;
    case 3:
      span = 4;
      break;
    case 4:
      span = 3;
      break;
    default:
      span = 4; // fallback for other counts
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="px-4 md:px-8 lg:px-12 xl:px-0 font-fellix bg-lightgrey py-16"
    >
      <div className="max-w-[1280px] mx-auto">
        {slice.primary.heading && (
          <article className="text-center my-8">
            <PrismicRichText field={slice.primary.heading} />
          </article>
        )}

        {slice.primary.description && (
          <article className="text-center text-[20px] leading-[28px] lg:text-[28px] lg:leading-[36px] mb-24">
            <PrismicRichText field={slice.primary.description} />
          </article>
        )}

        <div className="grid grid-cols-12 gap-4 mx-4">
          {people.map((item: any, index: number) => (
            <div
              key={index}
              className={`col-span-12 md:col-span-6 lg:col-span-${span} text-[18px] mb-12`}
            >
              <p className="font-semibold text-[20px] leading-[120%] py-4 mb-4 border-b-[1px] border-b-jade lg:pr-[10%] xl:pr-[28%] md:min-h-[80px]">
                {item.region}
              </p>
              <PrismicNextImage
                field={item.image}
                className="w-full aspect-4/3 object-cover mb-4"
                fallbackAlt=""
              />
              
              <p className="font-semibold text-[20px]">{item.name}</p>
              <p className="mb-4">{item.position}</p>
              <p className="text-[16px]">
                <a href={`tel:${item.phone}`}>{item.phone}</a>
              </p>
              <p className="text-[16px]">
                <a className="underline break-all" href={`mailto:${item.email}`}>{item.email}</a>
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PeopleGrid;
